import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useI18n } from './useI18n';
import { nominatimService } from '../services/geocoding/nominatimService';
import { GEOCODING_CONSTANTS } from '../constants/geocoding';
import { logger } from '../utils/logger';
import type { Coordinates } from '../types';

/**
 * Result of resolving a human-readable label for a set of coordinates.
 * `label` always carries a usable string — the localized place name on success,
 * or the `mapSetup.center.unknownPlace` placeholder on any failure / empty result.
 */
export interface ReverseGeocodeLabel {
  /** Localized place name, or the unknown-place placeholder. Never empty. */
  label: string;
  /** Whether a reverse-geocode request is currently in flight. */
  isLoading: boolean;
}

/**
 * Debounced, fail-safe reverse-geocoding for the map configurator center.
 *
 * Given the current center coordinates, it resolves a worldwide (unbounded)
 * place name via Nominatim after a debounce window. The hook NEVER throws and
 * NEVER blocks a Save flow: on error, rate-limit (429), empty result, or null
 * coordinates it falls back to the `mapSetup.center.unknownPlace` placeholder.
 *
 * @param coordinates - Center coordinates to label, or `null` while unset.
 */
export const useReverseGeocodeLabel = (coordinates: Coordinates | null): ReverseGeocodeLabel => {
  const { t } = useTranslation();
  const { currentLanguage } = useI18n();
  const [label, setLabel] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const requestIdRef = useRef(0);

  useEffect(() => {
    const placeholder = t('mapSetup.center.unknownPlace');

    if (!coordinates) {
      setLabel(placeholder);
      setIsLoading(false);
      return;
    }

    // Invalidate any in-flight request so stale responses are ignored.
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setIsLoading(true);

    const timeoutId = setTimeout(async () => {
      try {
        const result = await nominatimService.reverseGeocode({
          coordinates: { lat: coordinates.lat, lng: coordinates.lng },
          unbounded: true,
          language: currentLanguage,
        });

        if (requestIdRef.current !== requestId) {
          return;
        }

        const placeName = result.success ? result.data.features[0]?.place_name : undefined;
        setLabel(placeName ?? placeholder);
      } catch (error) {
        // Reverse-geocoding is best-effort UX only — never surface or rethrow.
        logger.error('Reverse geocode label lookup failed', 'MapSetup', { error });
        if (requestIdRef.current === requestId) {
          setLabel(placeholder);
        }
      } finally {
        if (requestIdRef.current === requestId) {
          setIsLoading(false);
        }
      }
    }, GEOCODING_CONSTANTS.REVERSE_GEOCODE_DEBOUNCE_MS);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [coordinates, currentLanguage, t]);

  return { label, isLoading };
};
